import { useEffect, useState } from "react";

/**
 * Feb 26 2026 · Attribution Demo Reel.
 *
 * A self-contained 30-second animated mock-up of the attribution
 * loop, embedded next to the marketing cards on the landing page.
 * Rather than shipping heavy screenshot GIFs (which age poorly
 * whenever the UI changes), we render a stylised HTML+CSS diorama
 * that cycles through four scenes:
 *
 *   1. Ad Copy Studio — three drafts side-by-side.
 *   2. Leaderboard    — ranked top-5 ads by revenue.
 *   3. Drill-through  — one pill highlighted, pipeline filters.
 *   4. Bulk Undo      — Toast surfaces with a 72h Undo action.
 *
 * All scenes render simultaneously in a stack, and CSS opacity
 * keyframes fade them in/out on a 30s loop. Zero JS animations, zero
 * external deps — cheap to render, easy to update.
 */
const SCENES = ["studio", "leaderboard", "drill", "undo"];
const SCENE_LABELS = {
  studio: "Ad Copy Studio drafts variants",
  leaderboard: "Leaderboard ranks by revenue",
  drill: "Drill-through filters pipeline",
  undo: "Every bulk action has 24-72h undo",
};

// Feb 26 2026 · Respect the OS-level `prefers-reduced-motion` hint.
// Founders who set this — or accessibility tools that enforce it —
// should NEVER see the auto-cycling scenes; instead we freeze on the
// leaderboard (the single most information-dense scene) and suppress
// every CSS keyframe on the diorama.
function useReducedMotion() {
  const [reduced, setReduced] = useState(() => {
    if (typeof window === "undefined" || !window.matchMedia) return false;
    return window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  });
  useEffect(() => {
    if (typeof window === "undefined" || !window.matchMedia) return undefined;
    const mq = window.matchMedia("(prefers-reduced-motion: reduce)");
    const onChange = (e) => setReduced(e.matches);
    // Older Safari uses addListener; modern browsers use addEventListener.
    if (mq.addEventListener) mq.addEventListener("change", onChange);
    else if (mq.addListener) mq.addListener(onChange);
    return () => {
      if (mq.removeEventListener) mq.removeEventListener("change", onChange);
      else if (mq.removeListener) mq.removeListener(onChange);
    };
  }, []);
  return reduced;
}

export default function AttributionDemoReel() {
  // Live captions the CSS-driven scenes tell the reader what's
  // happening — mirrors the same 4-beat rhythm.
  const reducedMotion = useReducedMotion();
  const LEADERBOARD_IDX = SCENES.indexOf("leaderboard");
  const [scene, setScene] = useState(reducedMotion ? LEADERBOARD_IDX : 0);
  useEffect(() => {
    if (reducedMotion) {
      setScene(LEADERBOARD_IDX);
      return undefined;
    }
    const iv = setInterval(() => setScene((i) => (i + 1) % SCENES.length), 7500);
    return () => clearInterval(iv);
  }, [reducedMotion, LEADERBOARD_IDX]);

  return (
    <div
      className="relative w-full max-w-[520px] mx-auto"
      data-testid="attribution-demo-reel"
      data-reduced-motion={reducedMotion ? "true" : "false"}
    >
      <style>{`
        @keyframes tlbReelStudio      { 0%,25% {opacity:1} 30%,100% {opacity:0} }
        @keyframes tlbReelLeaderboard { 0%,25% {opacity:0} 30%,50% {opacity:1} 55%,100% {opacity:0} }
        @keyframes tlbReelDrill       { 0%,50% {opacity:0} 55%,75% {opacity:1} 80%,100% {opacity:0} }
        @keyframes tlbReelUndo        { 0%,75% {opacity:0} 80%,100% {opacity:1} }
        @keyframes tlbReelBarGrow     { 0% {width:0%} 60% {width:var(--w,50%)} 100% {width:var(--w,50%)} }
        @keyframes tlbReelPulse       { 0%,80% {transform:scale(1)} 90% {transform:scale(1.08)} 100% {transform:scale(1)} }
        .tlb-reel-scene { position:absolute; inset:0; animation-duration:30s; animation-iteration-count:infinite; animation-timing-function:ease-in-out; }
        .tlb-reel-bar { animation: tlbReelBarGrow 30s ease-out infinite; animation-delay: 0.4s; }
        .tlb-reel-pulse { animation: tlbReelPulse 30s ease-in-out infinite; }
        /* Feb 26 2026 · Reduced-motion fallback. When the OS/browser
           reports prefers-reduced-motion, we kill every keyframe and
           hold the leaderboard scene as a static still. */
        [data-testid="attribution-demo-reel"][data-reduced-motion="true"] .tlb-reel-scene,
        [data-testid="attribution-demo-reel"][data-reduced-motion="true"] .tlb-reel-bar,
        [data-testid="attribution-demo-reel"][data-reduced-motion="true"] .tlb-reel-pulse {
          animation: none !important;
        }
        [data-testid="attribution-demo-reel"][data-reduced-motion="true"] .tlb-reel-scene {
          opacity: 0;
        }
        [data-testid="attribution-demo-reel"][data-reduced-motion="true"] [data-testid="attribution-demo-reel-scene-leaderboard"] {
          opacity: 1;
        }
        [data-testid="attribution-demo-reel"][data-reduced-motion="true"] .tlb-reel-bar {
          width: var(--w, 50%);
        }
        @media (prefers-reduced-motion: reduce) {
          [data-testid="attribution-demo-reel"] .tlb-reel-scene,
          [data-testid="attribution-demo-reel"] .tlb-reel-bar,
          [data-testid="attribution-demo-reel"] .tlb-reel-pulse {
            animation: none !important;
          }
        }
      `}</style>
      <div className="relative border-2 border-black bg-neutral-950 shadow-[8px_8px_0_0_rgba(0,0,0,0.9)]">
        {/* Fake browser chrome — sells the "this is a real product" vibe */}
        <div className="flex items-center gap-1.5 px-3 py-2 bg-neutral-900 border-b border-neutral-800">
          <span className="w-2.5 h-2.5 rounded-full bg-rose-500" />
          <span className="w-2.5 h-2.5 rounded-full bg-amber-500" />
          <span className="w-2.5 h-2.5 rounded-full bg-emerald-500" />
          <span className="ml-2 text-[10px] font-black uppercase tracking-[0.15em] text-neutral-400">
            three-little-birds · CRM
          </span>
        </div>
        {/* Scene stage — fixed aspect ratio so scenes never jump */}
        <div className="relative bg-white h-[320px] overflow-hidden">
          {/* Scene 1 · Ad Copy Studio */}
          <div
            className="tlb-reel-scene p-4"
            style={{ animationName: "tlbReelStudio" }}
            data-testid="attribution-demo-reel-scene-studio"
          >
            <div className="text-[9px] uppercase tracking-[0.2em] text-neutral-500 mb-1">Ad Copy Studio</div>
            <div className="font-display font-black text-sm tracking-tighter mb-3">3 platform-native variants, one click</div>
            <div className="grid grid-cols-3 gap-2">
              {[
                { p: "META", h: "Turn spare rooms into steady rent", t: "Three Little Birds helps you list smarter." },
                { p: "GOOGLE", h: "List Your Room In Minutes", t: "Free Three Little Birds setup · Cancel anytime" },
                { p: "TIKTOK", h: "POV: your room is now $$$", t: "Try Three Little Birds listing tool →" },
              ].map((v) => (
                <div key={v.p} className="border border-neutral-300 p-2 text-[10px] leading-tight">
                  <div className="text-[8px] uppercase tracking-[0.15em] font-black text-indigo-600 mb-1">{v.p}</div>
                  <div className="font-black mb-1">{v.h}</div>
                  <div className="text-neutral-600">{v.t}</div>
                </div>
              ))}
            </div>
            <div className="mt-3 text-[9px] uppercase tracking-[0.15em] text-emerald-700 font-black">
              ✓ Exported with UTMs — every click routes to your CRM
            </div>
          </div>

          {/* Scene 2 · Leaderboard */}
          <div
            className="tlb-reel-scene p-4"
            style={{ animationName: "tlbReelLeaderboard" }}
            data-testid="attribution-demo-reel-scene-leaderboard"
          >
            <div className="flex items-center justify-between mb-2">
              <div>
                <div className="text-[9px] uppercase tracking-[0.2em] text-neutral-500">Top ads by revenue</div>
                <div className="font-display font-black text-sm tracking-tighter">Which creative is printing money?</div>
              </div>
              <span className="border border-emerald-400 bg-emerald-50 text-emerald-900 text-[9px] uppercase tracking-[0.15em] font-black px-2 py-0.5">
                ● Health <span className="tabular-nums">78</span>/100
              </span>
            </div>
            {[
              { p: "META", n: "Turn spare rooms…", r: "$12,400", w: 100 },
              { p: "GOOGLE", n: "List Your Room In…", r: "$8,150", w: 66 },
              { p: "LINKEDIN", n: "Rent smarter with Three Little Birds", r: "$4,500", w: 36 },
              { p: "TIKTOK", n: "POV: your room…", r: "$2,900", w: 23 },
              { p: "META", n: "Weekend income tips", r: "$1,200", w: 10 },
            ].map((r, i) => (
              <div key={i} className="flex items-center gap-2 py-1.5 border-b border-neutral-100 last:border-b-0">
                <span className="tabular-nums text-[10px] font-black text-neutral-400 w-3">{i + 1}</span>
                <span className="text-[8px] uppercase tracking-[0.15em] font-black text-neutral-500 border border-neutral-200 px-1">{r.p}</span>
                <span className="text-[11px] font-semibold flex-1 truncate">{r.n}</span>
                <div className="w-16 h-1 bg-neutral-100 relative">
                  <div className="tlb-reel-bar absolute inset-y-0 left-0 bg-indigo-500" style={{ "--w": `${r.w}%` }} />
                </div>
                <span className="text-[10px] font-black tabular-nums w-14 text-right">{r.r}</span>
              </div>
            ))}
          </div>

          {/* Scene 3 · Drill-through */}
          <div
            className="tlb-reel-scene p-4"
            style={{ animationName: "tlbReelDrill" }}
            data-testid="attribution-demo-reel-scene-drill"
          >
            <div className="mb-2">
              <div className="text-[9px] uppercase tracking-[0.2em] text-neutral-500 mb-1">Filter pipeline by ad</div>
              <div className="flex gap-1.5 flex-wrap">
                {[
                  { p: "META", n: "Turn spare rooms…", selected: true },
                  { p: "GOOGLE", n: "List Your Room In…" },
                  { p: "LINKEDIN", n: "Rent smarter" },
                ].map((r, i) => (
                  <div
                    key={i}
                    className={`tlb-reel-pulse text-[10px] font-black border px-2 py-1 ${r.selected ? "border-indigo-600 bg-indigo-100 text-indigo-900" : "border-neutral-300 bg-white text-neutral-500"}`}
                  >
                    <span className="text-[8px] uppercase tracking-[0.15em] text-neutral-500 mr-1">{r.p}</span>
                    {r.n}
                  </div>
                ))}
              </div>
            </div>
            <div className="border border-indigo-400 bg-indigo-50 px-2 py-1 text-[10px] uppercase tracking-[0.15em] font-black text-indigo-900 mb-2">
              ▸ Showing <span className="tabular-nums">7</span> deals attributed to META · Turn spare…
            </div>
            <div className="grid grid-cols-3 gap-1.5">
              {["Lead", "Proposal", "Won"].map((s, si) => (
                <div key={s} className="border border-neutral-200 bg-neutral-50 p-1.5">
                  <div className="text-[9px] uppercase tracking-[0.15em] font-black text-neutral-500 mb-1">{s}</div>
                  {[0, 1, 2].slice(0, si === 2 ? 1 : si === 1 ? 2 : 3).map((di) => (
                    <div key={di} className="border border-neutral-300 bg-white text-[9px] p-1 mb-1 last:mb-0">
                      <div className="font-semibold truncate">Sample deal #{di + 1}</div>
                      <div className="text-[8px] text-emerald-600 font-black">META · v{di}</div>
                    </div>
                  ))}
                </div>
              ))}
            </div>
          </div>

          {/* Scene 4 · Bulk Undo Toast */}
          <div
            className="tlb-reel-scene p-4"
            style={{ animationName: "tlbReelUndo" }}
            data-testid="attribution-demo-reel-scene-undo"
          >
            <div className="text-[9px] uppercase tracking-[0.2em] text-neutral-500 mb-1">Bulk attribute mode · 12 selected</div>
            <div className="grid grid-cols-4 gap-1.5 mb-3">
              {Array.from({ length: 12 }).map((_, i) => (
                <div key={i} className="border-2 border-indigo-500 bg-indigo-50 p-1 text-[8px]">
                  <div className="text-indigo-700 font-black">☑</div>
                  <div className="font-semibold truncate">Deal {i + 1}</div>
                </div>
              ))}
            </div>
            <div className="tlb-reel-pulse bg-neutral-900 text-white p-2 shadow-lg flex items-center gap-2 max-w-[280px] mx-auto">
              <span className="text-emerald-400 text-sm">✓</span>
              <div className="flex-1 text-[10px] leading-tight">
                <div className="font-black">Attributed 12 deals to META v0</div>
                <div className="text-neutral-400 text-[9px]">Snapshot saved · one-tap undo</div>
              </div>
              <button className="text-[9px] uppercase tracking-[0.15em] font-black bg-indigo-500 hover:bg-indigo-400 text-white px-2 py-1">
                Undo (72h)
              </button>
            </div>
          </div>
        </div>
        {/* Caption strip + dot indicators */}
        <div className="flex items-center justify-between px-3 py-2 bg-neutral-950 border-t border-neutral-800">
          <div
            className="text-[10px] uppercase tracking-[0.15em] font-black text-neutral-300"
            data-testid="attribution-demo-reel-caption"
          >
            {reducedMotion ? "Static preview · reduced motion" : SCENE_LABELS[SCENES[scene]]}
          </div>
          <div className="flex items-center gap-1">
            {SCENES.map((s, i) => (
              <span
                key={s}
                className={`w-1.5 h-1.5 rounded-full transition-colors ${
                  i === scene ? "bg-indigo-400" : "bg-neutral-700"
                }`}
                data-testid={`attribution-demo-reel-dot-${i}`}
              />
            ))}
          </div>
        </div>
      </div>
      <div className="text-center text-[10px] uppercase tracking-[0.2em] text-neutral-500 mt-3">
        Ad Copy Studio → Attribution → Bulk Undo · Advanced
      </div>
    </div>
  );
}
